<template>
  <div class="DetailGrid__nav realisation-header">
    <Feedback
      :feedback="statusFeedback"
      :dismissible="false"
    />

    <StepsNav
      v-if="hasDepth"
      class="DetailGrid__nav"
      :step="step"
      :record="record"
    />
    <StepsNavFork
      v-else-if="hasBranches"
      :step="step"
      :steps="steps"
      :record="record"
      class="DetailGrid__nav mb-3"
    />
    <StepsNavSimple
      v-else
      :step="step"
      :steps="steps"
      :record="record"
      class="DetailGrid__nav mb-3"
    />

    <StepsNav
      v-if="currentStepIsGroup"
      class="DetailGrid__nav"
      :depth="2"
      :step="step"
      :record="record"
    />

    <b-navbar
      id="rp-header"
      class="mt-3"
      type="dark"
      variant="dark"
    >
      <OverlayPanel
        ref="op"
        append-to="rp-header"
      >
        {{ $t('RealisationHeader.sentence1') }}
      </OverlayPanel>
      <b-navbar-brand
        variant="light"
        :class="titleSize"
      >
        {{ (step + '').replace('-', ' ') }}. {{ $t(`${currentStepLabel}`) }}
      </b-navbar-brand>
      <b-navbar-nav class="ml-auto">
        <i
          v-if="canEdit"
          class="fas fa-circle-exclamation text-white fa-lg hover:text-gray"
          @click="handleOpen"
        />
        <slot />
      </b-navbar-nav>
    </b-navbar>

    <div class="w-100 p-1 px-3 header-info">
      <div v-if="editorRoles || viewerRoles">
        <span
          class="mr-3"
        >{{ $t('processes.headers.to_be_entered_by') }}: <strong>{{ editorRoles }}</strong>.</span>
        <span v-if="viewerRoles">{{ $t('processes.headers.for_inspection_by') }}: <strong>{{ viewerRoles }}</strong>.</span>
      </div>

      <div
        v-if="!missingDurationInformation"
        class="text-muted"
      >
        <span>{{ $t('processes.headers.started_on') }} {{ stepStartedOn }}</span>
        <span v-if="hasStepBeenCompleted">, {{ $t('processes.headers.finished_on') }} {{ stepFinishedOn }}</span>
        <span class="text-muted ml-2">({{ stepDurationLabel }})</span>
      </div>
      <div
        v-else
        class="text-muted"
      >
        {{ $t('processes.messages.missingDurationInformation') }}
      </div>
    </div>

    <b-alert
      class="mt-3"
      :show="!!feedback"
      :variant="feedback"
      :dismissible="true"
      @dismissed="$emit('dismissed')"
    >
      {{ feedbackMessage }}
    </b-alert>
  </div>
</template>

<script>
import StepsNav from '@/components/realisation/StepsNav.vue';
import StepsNavSimple from '@/components/realisation/StepsNavSimple.vue';
import StepsNavFork from '@/components/realisation/StepsNavFork.vue';
import Feedback from '@/components/form/Feedback.vue';
import OverlayPanel from 'primevue/overlaypanel';

import { roleToRoleLabel } from '@/config';
import { unique } from '@/helpers/array.js';
import { mapGetters } from 'vuex';
import { isValidTimestamp, msDurationToString, msToDate } from '@/helpers/date';

export default {
  name: 'RealisationHeader',
  components: { StepsNav, StepsNavSimple, StepsNavFork, Feedback, OverlayPanel },
  props: {
    step: {
      type: [String, Number],
      default: '1'
    },
    /**
     * The
     */
    recordStep: {
      type: [String, Number],
      default: 1
    },
    feedback: {
      type: String,
      default: null
    },
    feedbackMessage: {
      type: String,
      default: ''
    },
    record: {
      type: Object,
      required: false,
      default: () => {}
    },
    titleSize: {
      type: String,
      default: ''
    },
    mayEdit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('tenant', ['getOnHoldReasons', 'getCancelReasons', 'getSteps']),
    ...mapGetters('processes', ['getCurrentProcessType']),

    steps () {
      return this.getSteps
    },
    hasDepth() {
      return this.steps?.some(step => Array.isArray(step.children) && step.children.length !== 0);
    },
    hasBranches() {
      return this.steps?.some(step => !!step.branch)
    },
    currentStep() {
      return this.steps?.find(step => (step.step || Number(step.short)) === this.step) || {};
    },
    processType () {
      return this.getCurrentProcessType || 'realisation'
    },
    currentStepLabel() {
      return this.currentStep.label
        ? (this.currentStep.label[this.processType] || this.currentStep.label[[this.$i18n.locale]] || this.currentStep.label)
        : '';
    },
    canEdit () {
      return this.mayEdit
    },
    /**
     * If a step has children, it's a group, not a step by itself
     *  TODO: Fix naming. Code adjusted to also be true for children in group.
     */
    currentStepIsGroup() {
      if (!this.currentStep?.children) return false;

      if (Array.isArray(this.currentStep.children) && this.currentStep.children.length !== 0) {
        return true;
      }

      if (this.currentStep.short?.includes('-')) return true;

      return false;
    },
    viewerRoles() {
      if (!this.currentStep?.access) return '';

      let roles = this.currentStep.access.read
        .filter(role => role !== 'admin')
        .filter(role => !this.currentStep.access.edit.includes(role))
        .map(roleToRoleLabel);
      return unique({ arr: roles }).join(', ');
    },
    editorRoles() {
      if (!this.currentStep?.access) return '';

      let roles = this.currentStep.access.edit.filter(role => role !== 'admin').map(roleToRoleLabel);
      return unique({ arr: roles }).join(', ');
    },

    /**
     * Time indicators
     */
    stepDurationInfo() {
      return this.record.getStepDuractionByUuid({
        uuid: this.currentStep.uuid
      });
    },
    hasStepBeenCompleted() {
      return this.stepDurationInfo.finished;
    },
    missingDurationInformation() {
      return !isValidTimestamp(this.stepDurationInfo.start) ||
        (this.hasStepBeenCompleted && !isValidTimestamp(this.stepDurationInfo.end));
    },
    stepStartedOn() {
      return msToDate({
        ms: this.stepDurationInfo.start
      });
    },
    stepFinishedOn() {
      return msToDate({
        ms: this.stepDurationInfo.end
      });
    },
    stepDurationLabel() {
      const duration = this.stepDurationInfo;
      if (!isValidTimestamp(duration.start)) return '';

      const start = Number(duration.start);
      const end = this.hasStepBeenCompleted && isValidTimestamp(duration.end) ? Number(duration.end) : Date.now();
      const ms = end - start;

      return `${msDurationToString({ ms, locale: this.$i18n.locale, delimiter: this.$t('date.delimiter') })} ${this.hasStepBeenCompleted ? '' : this.$t('date.ago')}`;
    },

    /**
     * Status Feedback
     */
    statusFeedback() {
      if (!this.record) {
        return {
          show: false,
          message: ''
        };
      }

      if (this.record.status.cancelled) {
        let reason = this.getCancelReasons.find(reason => reason.value === this.record.statusMeta.cancelled.reason);
        reason = reason
          ? reason.text
          : this.record.statusMeta.cancelled.label || this.record.statusMeta.cancelled.reason;

        return {
          show: true,
          variant: 'danger',
          message: `${this.$i18n.t('processes.messages.cancelledWithReason')} "${reason}". ${this.record.statusMeta.cancelled.comment}`
        };
      } else if (this.record.status.onhold) {
        let reason = this.getOnHoldReasons.find(reason => reason.value === this.record.statusMeta.onhold.reason);
        reason = reason ? reason.text : this.record.statusMeta.onhold.label || this.record.statusMeta.onhold.reason;

        return {
          show: true,
          variant: 'warning',
          message: `${this.$i18n.t('processes.messages.onholdWithReason')} "${reason}". ${this.record.statusMeta.onhold.comment}`
        };
      }

      return {
        show: false
      };
    }
  },
  methods: {
    handleOpen(event) {
      this.$refs.op.toggle(event);
    }
  }
};
</script>

<style lang="scss">
.navbar-brand {
  &.sm {
    font-size: 1.1rem;
  }
}
.header-info {
  background: rgba(0, 0, 0, 0.05);
  box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
}
.compactsteps {
  padding-top: 4px !important;
  padding-bottom: 4px !important;

  .navbar-brand {
    font-size: 1rem !important;
    padding: 0;
  }
}
.DetailGrid__nav {
  &.realisation-header {
    position: sticky;
    top: $menu-height;
    background: white;
    z-index: 9;
    .p-overlaypanel {
      min-width: 100% !important;
      max-height: none;
      top: 100% !important;
      left: 0 !important;
    }
  }
  .bi-exclamation-circle-fill {
    color: #ced4da;
    cursor: pointer;
  }
  .navbar-nav {
    display: flex;
    align-items: center;
    gap: 0.7em;
    .btn {
      &.previous {
        order: 1;
      }
    }
    .btn:last-child,
    #next {
      order: 2;
    }
  }
}
</style>
